export const CAREER_FORM_ANCHOR = "#kd-form";
export const CAREER_FORM_SCROLL_GAP = 16;

export function careerFormScrollBehavior(
    reducedMotion: boolean,
): ScrollBehavior {
    return reducedMotion ? "auto" : "smooth";
}

export function careerFormScrollMargin(
    headerHeight: number,
    titleExtent: number,
    gap = CAREER_FORM_SCROLL_GAP,
) {
    return Math.max(0, headerHeight) + Math.max(0, titleExtent) + gap;
}

export interface CareerFormScrollTarget {
    id: string;
    previousElementSibling: {
        tagName: string;
        getBoundingClientRect: () => { top: number };
    } | null;
    getBoundingClientRect: () => { top: number };
    style: { scrollMarginTop: string };
    scrollIntoView: (options: ScrollIntoViewOptions) => void;
}

export interface CareerFormSmoothScroller {
    scrollTo: (
        target: CareerFormScrollTarget,
        options?: { offset?: number; immediate?: boolean },
    ) => void;
}

export function readCareerFormScrollMargin(
    form: CareerFormScrollTarget,
    header: { getBoundingClientRect: () => { height: number } } | null,
) {
    const formTop = form.getBoundingClientRect().top;
    const previous = form.previousElementSibling;
    const titleTop =
        previous && /^H[1-6]$/.test(previous.tagName)
            ? previous.getBoundingClientRect().top
            : null;
    const titleExtent = titleTop === null ? 0 : Math.max(0, formTop - titleTop);
    const headerHeight = header?.getBoundingClientRect().height ?? 0;
    return careerFormScrollMargin(headerHeight, titleExtent);
}

// Lenis owns window scrolling on this site, so a native smooth
// scrollIntoView is cancelled mid-animation. Lenis.scrollTo is the
// equivalent: same target, with offset standing in for scroll-margin.
export function scrollCareerFormIntoView({
    form,
    header,
    reducedMotion,
    lenis,
}: {
    form: CareerFormScrollTarget | null;
    header: { getBoundingClientRect: () => { height: number } } | null;
    reducedMotion: boolean;
    lenis?: CareerFormSmoothScroller | null;
}) {
    if (!form) return null;

    const behavior = careerFormScrollBehavior(reducedMotion);
    const scrollMargin = readCareerFormScrollMargin(form, header);
    form.style.scrollMarginTop = `${scrollMargin}px`;

    if (lenis && !reducedMotion) {
        lenis.scrollTo(form, { offset: -scrollMargin });
    } else {
        form.scrollIntoView({ behavior, block: "start" });
    }

    return { behavior, scrollMargin, targetId: form.id };
}
